// 캐릭터 선택·생성 화면의 표시 모델(docs/CHARACTER-PRD.md §3·§4·§13). DOM 없이 시험한다(characterScreen.ts가 그린다).
// 프레임 루프와 무관: 화면을 열 때·버튼을 누를 때만 부른다.
import { PLAYABLE_HEROES } from '../shared/types';
import { CLASS_IDS, CLASS_LABEL, formatHandle, nameReasonText, type CharacterFailure, type ClassId, type LocalCharacter, type NameCheck } from '../characters/index';
import { DELETE_GRACE_LEVEL } from '../characters/protocol';

/** 직업 카드 문구(얼굴 이름·역할·무기·난이도). 플레이 가능 여부는 PLAYABLE_HEROES(전투·모델이 준비된 직업)를 따른다 */
export interface ClassInfo { id: ClassId; label: string; face: string; role: string; weapon: string; stars: 1 | 2 | 3; playable: boolean; portrait: string; color: string }
const INFO: Record<ClassId, Omit<ClassInfo, 'id' | 'label' | 'playable' | 'portrait'>> = {
  kael: { face: '카엘', role: '근접 딜 · 몹몰이', weapon: '대검', stars: 1, color: '#e0313c' },
  lyra: { face: '라이라', role: '원거리 단일 · 기동', weapon: '장궁', stars: 2, color: '#3fd0c0' },
  seren: { face: '세렌', role: '광역 · 제어', weapon: '지팡이', stars: 3, color: '#b07cff' },
  brann: { face: '브란', role: '탱커 · 도발 · 보호막', weapon: '룬 해머', stars: 2, color: '#f2c94c' },
  lumen: { face: '루멘', role: '치유 · 보호 · 신성 딜', weapon: '성장 · 성서', stars: 2, color: '#fff1b8' },
};
export function isPlayableClass(cls: ClassId): boolean { return (PLAYABLE_HEROES as readonly string[]).includes(cls); }
export function classInfo(cls: ClassId): ClassInfo {
  return { id: cls, label: CLASS_LABEL[cls], playable: isPlayableClass(cls), portrait: 'ui/char/' + cls + '.webp', ...INFO[cls] };
}
/** 생성 화면 직업 카드 5장(PRD §2 순서) */
export function classCards(): ClassInfo[] { return CLASS_IDS.map(classInfo); }

/** 외형 색 변형(look 0..2) — 지금은 초상에 색조만 입힌다(3D 색 변형은 모델 레인) */
export const LOOK_NAMES = ['기본', '밤빛', '새벽빛'] as const;
export const LOOK_FILTERS = ['none', 'hue-rotate(200deg) saturate(.9)', 'hue-rotate(320deg) saturate(1.1) brightness(1.05)'] as const;
export function lookFilter(look: number): string { return LOOK_FILTERS[look] ?? 'none'; }

/** 이름 입력 실시간 안내 */
export function nameHint(check: NameCheck): { ok: boolean; text: string } {
  return check.ok ? { ok: true, text: '쓸 수 있는 이름이에요 · 만들면 서버가 #태그 4자리를 붙여요' } : { ok: false, text: nameReasonText(check.reason) };
}

export type FailureContext = 'list' | 'create' | 'rename' | 'delete' | 'restore' | 'select';
/** 서버·저장 실패 안내 문구(오류 코드 → 한 줄) */
export function failureText(f: CharacterFailure, ctx: FailureContext): string {
  switch (f.code) {
    case 'offline':
      return ctx === 'list' ? '서버에 닿지 않아 이 기기에 저장된 목록을 보여 줘요'
        : ctx === 'create' ? '서버에 닿지 않아 캐릭터를 만들 수 없어요(이름#태그 발급). 연결을 확인해 주세요'
        : '서버에 닿지 않았어요. 연결을 확인한 뒤 다시 시도해 주세요';
    case 'bad-name': return nameReasonText(f.reason);
    case 'slots-full': return '캐릭터 칸이 모두 찼어요. 다른 캐릭터를 정리한 뒤 만들어 주세요(칸 확장은 준비 중)';
    case 'name-full': return '이 이름은 태그를 더 받을 수 없어요. 다른 이름을 써 주세요';
    case 'rename-locked': return '무료 이름 변경을 이미 썼어요(추가 변경은 준비 중)';
    case 'character-deleted': return '삭제 대기 중인 캐릭터예요. 먼저 복구해 주세요';
    case 'not-restorable': return '복구 기간(7일)이 지나 복구할 수 없어요';
    case 'id-taken': return '다른 계정에 등록된 캐릭터예요. 계정 연결을 확인해 주세요';
    case 'not-found': return '서버에 없는 캐릭터예요. 목록을 다시 맞춰 주세요';
    case 'no-session': return '서버 연결을 준비하는 중이에요. 잠시 뒤 다시 시도해 주세요';
    case 'rate': return '요청이 너무 잦아요. 잠시 뒤 다시 시도해 주세요';
    case 'storage': return '이 브라우저 저장소를 쓸 수 없어 캐릭터를 바꿀 수 없어요';
    case 'error': return f.detail === 'conflict:mail-pending' ? '받다 만 우편이 있어요. 이 캐릭터로 우편함에서 먼저 받아 주세요' : f.detail === 'migration-busy' ? '캐릭터를 준비하는 중이에요. 잠시 뒤 다시 시도해 주세요' : '처리하지 못했어요' + (f.detail ? '(' + f.detail + ')' : '') + '. 잠시 뒤 다시 시도해 주세요';
  }
}

export type SlotState = 'ready' | 'download' | 'deleting' | 'localOnly' | 'locked';
export interface CharacterRow {
  id: string;
  cls: ClassId;
  look: number;
  /** 이름#태그(이름이 없으면 「이름 없음」) */
  title: string;
  unnamed: boolean;
  /** 「검사 · Lv 12」 */
  sub: string;
  level: number;
  state: SlotState;
  /** 상태 한 줄(받는 중·삭제 대기 n일·이 기기에만·준비 중 직업). ready면 빈 문자열 */
  stateText: string;
  current: boolean;
  canPlay: boolean;
  /** 이름 정하기(이름 없음 — 무료) · 바꾸기(1회 무료) · 잠김 */
  rename: 'name' | 'free' | 'locked';
  canDelete: boolean;
  /** 삭제가 7일 유예인가(Lv30+) */
  graceDelete: boolean;
  canRestore: boolean;
  /** 삭제 확인에 입력할 문구(이름, 이름이 없으면 'delete') */
  confirmWord: string;
}
export interface RowOptions {
  currentId: string | null;
  /** 게임 안(마을·타이틀)에서 연 화면: 지금 캐릭터는 삭제할 수 없다(다른 캐릭터로 바꾼 뒤) */
  inGame: boolean;
  now: number;
  levelOf: (c: LocalCharacter) => number;
}

/** 남은 유예(「6일 남음」 · 하루 미만이면 「n시간 남음」) */
export function purgeLeftText(purgeAt: number | null | undefined, now: number): string {
  if (!purgeAt) return '삭제 대기';
  const ms = purgeAt - now;
  if (ms <= 0) return '곧 영구 삭제';
  const days = Math.floor(ms / 86_400_000);
  return days >= 1 ? '삭제 대기 · ' + days + '일 남음' : '삭제 대기 · ' + Math.max(1, Math.ceil(ms / 3_600_000)) + '시간 남음';
}

export function characterRow(c: LocalCharacter, o: RowOptions): CharacterRow {
  const unnamed = !c.name || c.tag === null;
  const level = Math.max(1, Math.floor(o.levelOf(c)) || 1);
  const playable = isPlayableClass(c.cls);
  const deleting = !!c.deletedAt;
  const state: SlotState = deleting ? 'deleting' : c.needsDownload ? 'download' : !playable ? 'locked' : c.localOnly ? 'localOnly' : 'ready';
  const stateText = state === 'deleting' ? purgeLeftText(c.purgeAt, o.now)
    : state === 'download' ? '받는 중 · 서버 진행을 받으면 고를 수 있어요'
    : state === 'locked' ? '준비 중인 직업이에요'
    : state === 'localOnly' ? '이 기기에만 보관 중(계정 칸 부족)'
    : '';
  const current = c.id === o.currentId;
  return {
    id: c.id, cls: c.cls, look: c.look,
    title: unnamed ? '이름 없음' : formatHandle(c.name!, c.tag!),
    unnamed,
    sub: CLASS_LABEL[c.cls] + ' · Lv ' + level,
    level, state, stateText, current,
    canPlay: !deleting && !c.needsDownload && playable,
    rename: unnamed ? 'name' : c.renameFree === false ? 'locked' : 'free',
    // 이 기기에만 있는 캐릭터(서버에 없음)는 서버 삭제 대상이 아니다. 게임 안에서는 지금 캐릭터를 지울 수 없다
    canDelete: !deleting && c.registered && !c.localOnly && !(o.inGame && current),
    graceDelete: level >= DELETE_GRACE_LEVEL,
    canRestore: deleting,
    confirmWord: unnamed ? 'delete' : c.name!,
  };
}

export interface SlotSummary { rows: CharacterRow[]; used: number; cap: number; max: number; empty: number; text: string }
/** 슬롯 화면: 캐릭터 줄(슬롯 순) + 빈 칸 수 + 「칸 2/3」 */
export function slotSummary(chars: readonly LocalCharacter[], cap: number, max: number, o: RowOptions): SlotSummary {
  const rows = [...chars].sort((a, b) => a.slot - b.slot).map((c) => characterRow(c, o));
  // 계정 칸을 쓰는 캐릭터 = 서버 캐릭터(삭제 대기 포함). 이 기기에만 보관 중인 캐릭터는 칸을 쓰지 않는다
  const used = chars.filter((c) => !c.localOnly).length;
  const empty = Math.max(0, cap - used);
  return { rows, used, cap, max, empty, text: '칸 ' + used + '/' + cap + (cap < max ? ' · 최대 ' + max + '칸(확장 준비 중)' : '') };
}

/** 삭제 확인 입력이 맞는가(앞뒤 공백·NFC 무시) */
export function confirmMatches(input: string, word: string): boolean { return input.normalize('NFC').trim() === word; }
